import { useMemo } from 'react';
import type { BrandInsightsPageContext } from '@trinity/chat';
import { ChatContextType } from '@trinity/chat';
import { useAgentChatContextItem } from '../hooks/useAgentChatContextItem';
import { useSegmentationSessionId } from '../hooks/useSegmentationSessionId';
import { ChatContextProviders } from './ChatContextProviders';
import { ChatInterfaceNative } from './ChatInterfaceNative';

const HACKATHON_AGENT_IDENTIFIER = 'hackathon-agent';

/**
 * Container that resolves the hackathon-agent chat context and renders
 * `ChatInterfaceNative` wrapped in `ChatContextProviders`.
 * Use this for all non-message-generation sidebars.
 */
export function ChatInterfaceNativeContainer({
  onClose,
  segmentIdentifier,
  segmentationRunId,
  runName,
}: {
  onClose?: () => void;
  segmentIdentifier?: string | null;
  segmentationRunId?: number | null;
  runName?: string | null;
}): React.JSX.Element | null {
  const agentItem = useAgentChatContextItem(HACKATHON_AGENT_IDENTIFIER);
  const sessionId = useSegmentationSessionId(segmentationRunId ?? null);

  const contextData = useMemo<BrandInsightsPageContext.Agent | null>(() => {
    if (!agentItem) return null;
    return {
      type: ChatContextType.Agent,
      data: agentItem,
      metadata: {
        ...(sessionId ? { sessionId } : {}),
        ...(segmentIdentifier ? { segmentIdentifier } : {}),
        ...(runName ? { runName } : {}),
      },
    };
  }, [agentItem, sessionId, segmentIdentifier, runName]);

  if (!contextData) return null;

  return (
    <ChatContextProviders contextData={contextData}>
      <ChatInterfaceNative onClose={onClose} />
    </ChatContextProviders>
  );
}
